<script setup lang="ts">
/**
 * One status chip for the whole admin surface.
 *
 * There were four, mutually incompatible: Governance's had a dot and called
 * a failure `gap`, Data sources' had no dot and called the same thing `bad`,
 * AI Requests and Logs each hand-rolled their own badge with their own
 * greens and ambers. Nine files between them typed five different ambers.
 * The result was that the same state looked different depending on which tab
 * you were standing on — which is the one thing a status indicator may not
 * do, because recognising it at a glance is its entire job.
 *
 * `tone` is the VISUAL state and is deliberately separate from the words.
 * Governance's vocabulary (ok / warn / gap / na) is load-bearing — ADR 0050
 * chose those words and the evidence report prints them — so the caller
 * keeps saying "gap" while the chip renders `tone="bad"`. Domain language
 * belongs to the domain; colour belongs here.
 *
 * Never colour alone: the label is the message, the tone only reinforces it.
 * A chip with no text is a bug, not a compact chip.
 */
withDefaults(
  defineProps<{
    tone?: "ok" | "warn" | "bad" | "info" | "neutral" | "muted";
    /** A leading dot. Use in dense tables where the word alone scans slowly. */
    dot?: boolean;
  }>(),
  { tone: "neutral", dot: false },
);
</script>

<template>
  <span :class="['status-chip', `status-chip--${tone}`]">
    <span v-if="dot" class="status-chip__dot" aria-hidden="true" />
    <slot />
  </span>
</template>

<style scoped>
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.05rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.5;
  border-radius: var(--radius-pill);
  white-space: nowrap;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-ink);
}
.status-chip__dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}
.status-chip--ok {
  background: var(--color-success-bg);
  color: var(--color-success);
}
.status-chip--warn {
  background: var(--color-warn-bg);
  color: var(--color-warn-ink);
}
.status-chip--bad {
  background: var(--color-error-bg);
  color: var(--color-error);
}
.status-chip--info {
  background: var(--color-accent-dim);
  color: var(--color-accent-hover);
}
.status-chip--muted {
  background: transparent;
  color: var(--color-muted);
  font-weight: 500;
}
</style>
